import { Link, useLocation } from "wouter";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  PieChart,
  FileText,
  ArrowUpDown,
  MessageSquare,
  Bell,
  LogOut,
  Home,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { SidebarBrand } from "@/components/public/logo-mark";
import { useCloseMobileSidebarOnNavigate } from "@/hooks/use-close-mobile-sidebar";

interface Notification {
  id: string;
  isRead: boolean;
}

const investorNavItems = [
  { icon: LayoutDashboard, label: "Dashboard", href: "/dashboard" },
  { icon: PieChart, label: "Portfolio", href: "/dashboard/portfolio" },
  { icon: FileText, label: "Statements", href: "/dashboard/statements" },
  { icon: ArrowUpDown, label: "Transactions", href: "/dashboard/transactions", badge: null },
  { icon: MessageSquare, label: "Support", href: "/dashboard/support", badge: null },
  { icon: Bell, label: "Announcements", href: "/dashboard/announcements", badge: "announcements" },
];

export function InvestorSidebar() {
  const [location] = useLocation();
  const { user, logout } = useAuth();
  useCloseMobileSidebarOnNavigate();

  // Fetch unread announcements count
  const { data: unreadData } = useQuery<{ count: number }>({
    queryKey: ["/api/investor/unread-announcements"],
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const unreadCount = unreadData?.count || 0;

  const initials = user
    ? `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase() || user.email?.[0]?.toUpperCase() || "U"
    : "U";

  // Fetch dashboard data for client ID
  const { data: dashboardData } = useQuery<any>({
    queryKey: ["/api/investor/dashboard"],
  });

  const clientId = dashboardData?.profile?.clientId || (user as any)?.clientId;

  return (
    <Sidebar className="border-r">
      <SidebarHeader className="p-4">
        <SidebarBrand panelLabel="Investor Panel" />
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          {/* <SidebarGroupLabel>Investor Panel</SidebarGroupLabel> */}
          <SidebarGroupContent>
            <SidebarMenu>
              {investorNavItems.map((item) => {
                const count = item.badge === "announcements" ? unreadCount : 0;
                return (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton
                      asChild
                      isActive={location === item.href}
                      data-testid={`sidebar-link-${item.label.toLowerCase()}`}
                    >
                      <Link href={item.href} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <item.icon className="w-4 h-4" />
                          <span>{item.label}</span>
                        </div>
                        {count > 0 && (
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-medium text-destructive-foreground">
                            {count > 99 ? "99+" : count}
                          </span>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Quick Links</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild data-testid="sidebar-link-website">
                  <Link href="/">
                    <Home className="w-4 h-4" />
                    <span>Back to Website</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-4 border-t border-sidebar-border">
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarImage src={user?.profileImageUrl || undefined} />
            <AvatarFallback className="bg-sidebar-accent text-sidebar-accent-foreground text-sm">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-sidebar-foreground truncate">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-xs text-sidebar-foreground/60 truncate">
              {user?.email}
            </p>
            {clientId && (
              <p className="text-[11px] font-mono text-accent font-semibold truncate">
                Investor ID: {clientId}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => logout()}
            className="text-sidebar-foreground/60 hover:text-sidebar-foreground"
            data-testid="button-logout"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
